import { useEffect } from 'react';
import { HubConnectionBuilder } from '@microsoft/signalr';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { ApiEndpoints } from '../../../constants/endpoints/apiEndpoints';

type FolioRequestsHubServer = {
  OnAssignedChanged: () => void;
  OnCompletedChanged: () => void;
};

/**
 * FolioRequestsHub
 *
 * Listens for real-time updates to folio requests (assignment + completion)
 * and keeps the folio follow-up React Query caches in sync across users.
 */
export const FolioRequestsHub = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    const connection = new HubConnectionBuilder()
      .withUrl(ApiEndpoints.hubs.folioRequests)
      .withAutomaticReconnect([0, 2000, 10000, 30000, 60000, 120000])
      .build();

    const handleChange = async () => {
      await queryClient.invalidateQueries({
        queryKey: ['folioCollectionHotels'],
        type: 'all'
      });
      await queryClient.invalidateQueries({
        queryKey: ['folioCollectionHotelDetail'],
        type: 'all'
      });
      await queryClient.invalidateQueries({
        queryKey: ['folioCollectionHotelHistory'],
        type: 'all'
      });
      await queryClient.invalidateQueries({
        queryKey: ['folioCollectionActivityDetail'],
        type: 'all'
      });
    };

    const rpc: FolioRequestsHubServer = {
      OnAssignedChanged: handleChange,
      OnCompletedChanged: handleChange
    };

    const functions = Object.keys(rpc) as (keyof FolioRequestsHubServer)[];
    for (const procedure of functions) {
      connection.on(procedure, rpc[procedure]);
    }

    connection.start().catch((e) => {
      Sentry.captureException(e);
    });

    connection.onreconnected(() => {
      void handleChange();
    });

    return () => {
      connection.stop().catch(() => {
        // Ignore stop errors on unmount
      });
    };
  }, [queryClient]);

  return null;
};
